import { environment } from "../config/environment";
import { getFromBackendAPI, postToBackendAPI, putToBackendAPI } from "./api.service";

/**
 * Xero organisation summary
 */
export interface XeroOrgSummary {
  orgName: string;
  tenantId: string;
}

/**
 * Response from the connected-orgs endpoint
 */
export interface GetXeroOrgsResponse {
  organisations: XeroOrgSummary[];
}

/**
 * Xero account with report code mapping
 */
export interface XeroAccount {
  id: number;
  xeroAccountId: string;
  code: string | null;
  name: string;
  type: string | null;
  class: string | null;
  reportCode: string | null;
  mappingStatus: "mapped" | "unmapped" | "needs_review";
  bdoWorkpaperCode: string | null;
  bdoWorkpaperCodeOverride: string | null;
}

/**
 * Get all Xero organisations connected to the user's office
 * @returns Promise with array of Xero organisations
 */
export async function getConnectedXeroOrgs(): Promise<XeroOrgSummary[]> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.xeroIntegration}/connected-orgs`;
  const response = await getFromBackendAPI<GetXeroOrgsResponse>(endpoint);
  return response.organisations || [];
}

/**
 * Get all Xero accounts for a tenant
 * @param tenantId - The Xero tenant ID
 * @returns Promise with array of Xero accounts
 */
export async function getXeroAccounts(tenantId: string): Promise<XeroAccount[]> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.xeroIntegration}/accounts/tenant/${tenantId}`;
  return await getFromBackendAPI<XeroAccount[]>(endpoint);
}

/**
 * Sync Xero accounts from Xero API for a tenant
 * @param tenantId - The Xero tenant ID
 * @returns Promise with array of synced Xero accounts
 */
export async function syncXeroAccounts(tenantId: string): Promise<XeroAccount[]> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.xeroIntegration}/accounts/tenant/${tenantId}/sync`;
  return await postToBackendAPI<XeroAccount[]>(endpoint, {});
}

/**
 * Update the report code mapping for a Xero account
 * @param accountId - The account ID
 * @param reportCode - The new report code (or null to clear)
 */
export async function updateAccountMapping(accountId: number, reportCode: string | null): Promise<void> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.xeroIntegration}/accounts/${accountId}`;
  await putToBackendAPI(endpoint, { reportCode });
}

/**
 * Bulk update report code mappings for multiple accounts
 * @param tenantId - The Xero tenant ID
 * @param updates - Array of account ID and report code pairs
 */
export async function bulkUpdateAccountMappings(
  tenantId: string,
  updates: Array<{ id: number; reportCode: string | null }>
): Promise<void> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.xeroIntegration}/accounts/tenant/${tenantId}/bulk`;
  await putToBackendAPI(endpoint, updates);
}

/**
 * Update the BDO workpaper code override for a Xero account
 * @param accountId - The account ID
 * @param bdoWorkpaperCode - The override BDO code (or null to clear and revert to default mapping)
 */
export async function updateBdoOverride(accountId: number, bdoWorkpaperCode: string | null): Promise<void> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.xeroIntegration}/accounts/${accountId}/bdo-override`;
  await putToBackendAPI(endpoint, { bdoWorkpaperCode });
}

/**
 * Trial balance row from Xero
 */
export interface TrialBalanceRowDto {
  accountCode: string;
  accountName: string;
  accountType: string;
  debit: number;
  credit: number;
  balance: number;
}

/**
 * A single comparison period within a trial balance response
 */
export interface ComparisonPeriodDto {
  label: string;
  date: string | null;
  rows: TrialBalanceRowDto[];
}

/**
 * Trial balance response with organisation context
 */
export interface TrialBalanceResponse {
  organisationName: string;
  balanceDate: string | null;
  rows: TrialBalanceRowDto[];
  priorYearRows: TrialBalanceRowDto[];
  comparisonPeriods: ComparisonPeriodDto[];
}

/**
 * Get the trial balance for a spreadsheet's linked Xero organisation
 * @param spreadsheetId - The spreadsheet ID
 * @param periodCount - Number of comparison periods to fetch (default: 1)
 * @param periodType - Type of comparison period: "years" or "months" (default: "years")
 * @returns Promise with trial balance data including org name and rows
 */
export async function getTrialBalance(
  spreadsheetId: string,
  periodCount: number = 1,
  periodType: string = "years"
): Promise<TrialBalanceResponse> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.xeroIntegration}/trial-balance/spreadsheet/${spreadsheetId}?periodCount=${periodCount}&periodType=${periodType}`;
  return await getFromBackendAPI<TrialBalanceResponse>(endpoint);
}

/**
 * Account balance within a workpaper
 */
export interface AccountBalanceDto {
  accountCode: string;
  accountName: string;
  accountType: string;
  debit: number;
  credit: number;
  balance: number;
}

/**
 * Workpaper balance with underlying account details
 */
export interface WorkpaperBalanceDto {
  bdoWorkpaperCode: string;
  debit: number;
  credit: number;
  balance: number;
  accounts: AccountBalanceDto[];
}

/**
 * Get workpaper balances for a spreadsheet
 * Returns balances grouped by BDO workpaper code with account details
 * @param spreadsheetId - The spreadsheet ID
 * @returns Promise with array of workpaper balances (empty if no Xero connection)
 */
export async function getWorkpaperBalances(spreadsheetId: string): Promise<WorkpaperBalanceDto[]> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.xeroIntegration}/workpaper-balances/spreadsheet/${spreadsheetId}`;
  return await getFromBackendAPI<WorkpaperBalanceDto[]>(endpoint);
}
